/**
 *
 * Maintenance page to view in desktop sized screen with 3D deck canvas
 *
 * @file   MaintenancePage.js
 * @author Lateral
 * @since  2023
 */
import { Button, Typography, Stack, useTheme, Box, Tooltip } from '@mui/material'
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useCurrentUser, usePrompt } from 'hooks'
import {
  useCurrentDeckContext,
  defaultMaintenanceData,
  ONE_WEEK_MILLIS
} from 'components/currentDeckContext/CurrentDeckContext'
import { createDeckRevisionHistory, updateDeckRevisionHistory } from '../common/historyData'
import CreateHistory from './components/CreateHistory/CreateHistory'
import dayjs from 'dayjs'
import { loseDataWarning } from 'common/messaging'
import { Storage } from 'aws-amplify'
import { Outlet } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { addOtherKitMaintenance, addWearAppAction } from 'pages/common/maintenance/logic'
import { useNetworkState, useWindowSize } from 'react-use'
import { useBackgroundProcess } from 'context/BackgroundProcessContext'
import { useSnackbar } from 'notistack'
import { groupBy } from 'common/arrays'
import { OtherKitsMaintenance } from './components/OtherKitsMaintenance'
import { ScrollProvider, useScrollToOtherKits } from './components/ScrollContext'
import { ExportWearData } from './components/ExportWearData'

const sideLinerName = 'sideliner'
const panelName = 'panel'
const otherName = 'other'

/*
 *Page for all things Maintenance. Shows the 3D canvas and SummaryForm.
 */
function MaintenancePageContent() {
  /**
   * Generates Maintenance page with 3D cube panels with maintenance components
   *
   * @function
   *
   * @returns {object} - Maintenance page
   */
  const { getUserName, getTransactionMetaData } = useCurrentUser()
  const { scrollToOtherKits } = useScrollToOtherKits()
  const {
    deck,
    memoizedDeckRevision: deckRevision,
    history,
    maintenanceData,
    inMaintenanceMode,
    setInMaintenanceMode,
    setMaintenanceData,
    database,
    actions,
    wearAnalysisResults,
    nonArchivedHistories,
    maintenanceFrequency,
    setSelectedPanel,
    setGridHeight,
    setCurrentMaintenanceView
  } = useCurrentDeckContext()

  const [showExportWearData, setShowExportWearData] = useState(false)

  const maintenanceHistories = useMemo(() => {
    return nonArchivedHistories.histories.filter(
      (h) => h.HistoryType === 'Maintenance' && h.Details.some((d) => d.WearAppResponse === 'add-wear-action')
    )
  }, [nonArchivedHistories.histories])

  const { online } = useNetworkState()

  const { addProcess } = useBackgroundProcess()

  const { enqueueSnackbar } = useSnackbar()
  const { state, pathname } = useLocation()
  const isEditingOld = state?.isEditingOld && maintenanceData !== defaultMaintenanceData
  const theme = useTheme()

  const [showOtherKits, setShowOtherKits] = useState(false)

  const sideLiners = useMemo(() => {
    return groupBy('MaterialNumber')(deckRevision?.SideLiners)
  }, [deckRevision?.SideLiners, deckRevision?.SideLiners?.length])

  const panels = useMemo(() => {
    return groupBy('MaterialNumber')(deckRevision?.Panels)
  }, [deckRevision?.Panels, deckRevision?.Panels?.length])

  const kits = useMemo(() => {
    return [...(deckRevision?.Kits ?? [])].sort((a, b) => a.MaterialNumber.localeCompare(b.MaterialNumber))
  }, [deckRevision?.Kits, deckRevision?.Kits?.length])

  function panelTypeCheck(material) {
    const panelKeys = Object.keys(panels)
    const sideLinerKeys = Object.keys(sideLiners)

    if (panelKeys.includes(material)) {
      return panelName
    }

    if (sideLinerKeys.includes(material)) {
      return sideLinerName
    }

    return otherName
  }

  //TODO: ask for when it's appropriate to navigate
  usePrompt(loseDataWarning, false)

  // Start deck maintenance
  function onStart() {
    const clone = structuredClone(maintenanceData)
    clone.deckRevisionHistory.Details = []
    if (!clone.deckRevisionHistory.DatePerformed) {
      clone.deckRevisionHistory.DatePerformed = dayjs().toISOString()
    }
    if (!clone.deckRevisionHistory.NextShutDownDate) {
      clone.deckRevisionHistory.NextShutDownDate = dayjs(nonArchivedHistories.histories[0]?.DatePerformed || dayjs())
        .add((maintenanceFrequency || 0) * ONE_WEEK_MILLIS, 'milliseconds')
        .toISOString()
    }

    setInMaintenanceMode(true)
    setMaintenanceData(clone)
    history.addHistory(clone)
    setCurrentMaintenanceView(undefined)
  }

  // Action on 'Completing Maintenance' button
  const isWearAppFlow = pathname.includes('wearapp')

  async function onComplete(data) {
    let clone = structuredClone(maintenanceData)

    // get all 'unsaved' wearapp results
    const wearAppResults = clone.deckRevisionHistory.Details.filter(
      ({ HistoryAction }) => HistoryAction === actions.ViewWearAppResult.id
    )

    for (const result of wearAppResults) {
      const wearAppResult = wearAnalysisResults[result.extraData.uuid]
      clone = addWearAppAction(
        wearAppResult,
        clone,
        result.Panel,
        wearAppResult.replacePanel?.replacePanel === 'replace' ? actions.Replace.id : actions.NoChange.id,
        deckRevision
      )
    }
    clone.deckRevisionHistory.RevisionId = deckRevision.id
    clone.deckRevisionHistory.RevisionNumber = 1
    clone.deckRevisionHistory.HistoryType = 'Maintenance'

    clone.deckRevisionHistory.CustomerId = deck.CustomerId
    clone.deckRevisionHistory.SiteId = deck.SiteId

    let photoKeys = []

    const getPhotos = async (type, photos) => {
      const photoKeys = []

      for (let i = 0; i < photos?.length; i++) {
        const photo = photos[i]

        //if the photo already has a key, it was uploaded beforehand. No need to upload again
        if (photo.key) {
          photoKeys.push({ Key: photo.key, Caption: photo.caption, Height: photo.height, Width: photo.width })
        } else {
          const fileName = `${deckRevision.id}-${type}-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}-${i}.png`

          if (online) {
            try {
              const result = await Storage.put(fileName, photo.file, {
                metadata: { original: 'true' }
              })
              photoKeys.push({
                Key: result.key,
                Caption: photo.caption,
                Height: photo.height,
                Width: photo.width
              })
            } catch {
              enqueueSnackbar('Could not upload photos!', {
                variant: 'error'
              })
              return
            }
          } else {
            photoKeys.push({
              Key: fileName,
              Caption: photo.caption,
              Height: photo.height,
              Width: photo.width
            })

            await addProcess({
              type: 'file-upload',
              path: fileName,
              file: photo.file
            })
          }
        }
      }

      return photoKeys
    }

    clone.deckRevisionHistory.Details = clone.deckRevisionHistory.Details.map((data) => {
      if (data.extraData) {
        delete data.extraData
      }
      return data
    })

    clone.deckRevisionHistory.OtherKitsMaintenance = clone.deckRevisionHistory.OtherKitsMaintenance.map((data) => {
      data.Quantity = Number(data.Quantity)
      return data
    })

    photoKeys = await getPhotos('pre-service', clone.photos ?? [])
    photoKeys = photoKeys.concat(await getPhotos('mid-service', clone.midServicePhotos ?? []))
    photoKeys = photoKeys.concat(await getPhotos('post-service', clone.postServicePhotos ?? []))

    clone.deckRevisionHistory.Photos = photoKeys

    if (isEditingOld) {
      if (!clone.deckRevisionHistory.Updates) {
        clone.deckRevisionHistory.Updates = []
      }
      clone.deckRevisionHistory.Updates.push({
        User: getUserName(),
        Date: dayjs().toISOString(),
        Description: data.data.Description
      })
      const existing = database.deckRevisionHistories.find((d) => d.id === clone.deckRevisionHistory.id)

      if (existing) {
        await updateDeckRevisionHistory(existing, clone.deckRevisionHistory, getTransactionMetaData())
        enqueueSnackbar('Successfully updated Maintenance.')
      } else {
        enqueueSnackbar('Maintenance not found!', {
          variant: 'error'
        })
      }
    } else {
      clone.deckRevisionHistory.CreatedBy = getUserName()
      clone.deckRevisionHistory.ShortDescription = data.data.Description
      await createDeckRevisionHistory(clone.deckRevisionHistory, getTransactionMetaData())
      enqueueSnackbar('Successfully created Maintenance.')
    }
    setInMaintenanceMode(false)

    setMaintenanceData(defaultMaintenanceData)
    history.clearHistory()
  }

  // Cancelling maintenance
  function onCancel() {
    setSelectedPanel([])
    setInMaintenanceMode(false)
    enqueueSnackbar('Maintenance Cancelled.')
    setMaintenanceData(defaultMaintenanceData)
  }

  const containerRef = useRef()
  const window = useWindowSize()
  const otherKits = useMemo(() => {
    return kits
      .filter((k) => panelTypeCheck(k.MaterialNumber) === otherName)
      .filter((k) => {
        return k.Quantity > 0
      })
  }, [kits])

  useLayoutEffect(() => {
    setGridHeight(containerRef.current?.clientHeight)
  }, [window, setGridHeight])

  //do not show data if Deck and related variables missing.
  if (!deck) {
    return null
  }

  if ((deck && !deckRevision) || !Object.entries(deck).length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h3">No Deck Revision found.</Typography>
      </Box>
    )
  }

  const Container = ({ children }) =>
    isWearAppFlow ? (
      children
    ) : (
      <Box
        boxShadow="0px 10px 15px -3px rgba(0,0,0,0.1);"
        position="sticky"
        padding=".4em"
        top={62}
        zIndex={1200}
        bgcolor={theme.palette.supporting.dark}>
        {children}
      </Box>
    )

  return (
    <>
      <Container>
        <Stack ref={containerRef} direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Typography variant={isWearAppFlow ? 'h4' : 'h1'}>Maintenance</Typography>
          {inMaintenanceMode ? (
            !isWearAppFlow && (
              <CreateHistory
                isEditingOld={isEditingOld}
                onComplete={onComplete}
                onCancel={onCancel}
                hasOtherPanels={otherKits.length > 0}
                deckRevision={deckRevision}
                onShowOtherKits={() => setShowOtherKits(true)}
              />
            )
          ) : (
            <Box display="flex" gap={2}>
              {maintenanceHistories.length > 0 && (
                <Tooltip title="Export maintenance wear data for all panels to an excel file">
                  <span>
                    <Button variant="outlined" color="primary" onClick={() => setShowExportWearData(true)}>
                      Export Wear Data
                    </Button>
                  </span>
                </Tooltip>
              )}

              <Button variant="contained" color="secondary" onClick={onStart}>
                Start Maintenance
              </Button>
            </Box>
          )}
          {showOtherKits && (
            <OtherKitsMaintenance
              otherKits={otherKits}
              onClose={() => {
                setShowOtherKits(false)
              }}
              onSubmit={(data) => {
                setMaintenanceData(addOtherKitMaintenance(data, maintenanceData, actions.Replace.id))
                setShowOtherKits(false)
                // Scroll to the OtherKitsMaintenanceTable after submitting
                setTimeout(() => scrollToOtherKits(), 100)
              }}
            />
          )}
          {showExportWearData && (
            <ExportWearData
              open={showExportWearData}
              onClose={() => setShowExportWearData(false)}
              histories={maintenanceHistories}
            />
          )}
        </Stack>
      </Container>

      <Outlet />
    </>
  )
}

function MaintenancePage() {
  return (
    <ScrollProvider>
      <MaintenancePageContent />
    </ScrollProvider>
  )
}

export default MaintenancePage
